import { log } from '../logger/logger.js';
import { setCurrentFramework } from './getCurrentFramework.js';
import { isGuardianDomain } from './lib/domain.js';
import { mark } from './lib/mark.js';
import { ENDPOINT, ACCOUNT_ID, PROPERTY_ID_AUSTRALIA, PROPERTY_ID } from './lib/sourcepointConfig.js';
import { invokeCallbacks } from './onConsentChange.js';
import { stub } from './stub.js';

let resolveWillShowPrivacyMessage;
const willShowPrivacyMessage = new Promise((resolve) => {
  resolveWillShowPrivacyMessage = resolve;
});
const getPropertyHref = (framework) => {
  if (framework == "aus") {
    return "https://au.theguardian.com";
  }
  return isGuardianDomain() ? null : "https://test.theguardian.com";
};
const getPropertyId = (framework) => {
  if (framework == "aus") {
    return PROPERTY_ID_AUSTRALIA;
  }
  return PROPERTY_ID;
};
const init = (framework, pubData = {}) => {
  stub(framework);
  if (window._sp_) {
    throw new Error("Sourcepoint global (window._sp_) is already defined!");
  }
  setCurrentFramework(framework);
  invokeCallbacks();
  let frameworkMessageType;
  switch (framework) {
    case "usnat":
      frameworkMessageType = "usnat";
      break;
    case "aus":
      frameworkMessageType = "ccpa";
      break;
    case "tcfv2":
    default:
      frameworkMessageType = "gdpr";
      break;
  }
  const isInPropertyIdABTest = window.guardian?.config?.tests?.useSourcepointPropertyIdVariant === "variant";
  log("cmp", `framework: ${framework}`);
  log("cmp", `frameworkMessageType: ${frameworkMessageType}`);
  window._sp_queue = [];
  window._sp_ = {
    config: {
      baseEndpoint: ENDPOINT,
      accountId: ACCOUNT_ID,
      propertyHref: getPropertyHref(framework),
      targetingParams: {
        framework
      },
      pubData: { ...pubData, cmpInitTimeUtc: (/* @__PURE__ */ new Date()).getTime() },
      // ccpa or gdpr object added below
      events: {
        onConsentReady: (message_type, consentUUID, euconsent) => {
          log("cmp", `onConsentReady ${message_type}`);
          if (message_type != frameworkMessageType) {
            return;
          }
          log("cmp", `consentUUID ${consentUUID}`);
          log("cmp", `euconsent ${euconsent}`);
          mark("cmp-got-consent");
          setTimeout(invokeCallbacks, 0);
        },
        onMessageReady: (message_type) => {
          log("cmp", `onMessageReady ${message_type}`);
          if (message_type != frameworkMessageType) {
            return;
          }
          mark("cmp-ui-displayed");
        },
        onMessageReceiveData: (message_type, data) => {
          log("cmp", `onMessageReceiveData ${message_type}`);
          if (message_type != frameworkMessageType) {
            return;
          }
          log("cmp", "onMessageReceiveData ", data);
          void resolveWillShowPrivacyMessage(data.messageId !== 0);
        },
        onMessageChoiceSelect: (message_type, choice_id, choiceTypeID) => {
          log("cmp", `onMessageChoiceSelect message_type: ${message_type}`);
          if (message_type != frameworkMessageType) {
            return;
          }
          log("cmp", `onMessageChoiceSelect choice_id: ${choice_id}`);
          log("cmp", `onMessageChoiceSelect choice_type_id: ${choiceTypeID}`);
          if (
            // https://documentation.sourcepoint.com/web-implementation/web-implementation/multi-campaign-web-implementation/event-callbacks#choice-type-id-descriptions
            choiceTypeID === 11 || choiceTypeID === 13 || choiceTypeID === 15
          ) {
            setTimeout(invokeCallbacks, 0);
          }
        },
        onPrivacyManagerAction: function(message_type, pmData) {
          log("cmp", `onPrivacyManagerAction message_type: ${message_type}`);
          if (message_type != frameworkMessageType) {
            return;
          }
          log("cmp", `onPrivacyManagerAction ${pmData}`);
        },
        onMessageChoiceError: function(message_type, err) {
          log("cmp", `onMessageChoiceError ${message_type}`);
          if (message_type != frameworkMessageType) {
            return;
          }
          log("cmp", `onMessageChoiceError ${err}`);
        },
        onPMCancel: function(message_type) {
          log("cmp", `onPMCancel ${message_type}`);
          if (message_type != frameworkMessageType) {
            return;
          }
        },
        onSPPMObjectReady: function() {
          log("cmp", "onSPPMObjectReady");
        },
        onError: function(message_type, errorCode, errorObject, userReset) {
          log("cmp", `errorCode: ${message_type}`);
          if (message_type != frameworkMessageType) {
            return;
          }
          log("cmp", `errorCode: ${errorCode}`);
          log("cmp", errorObject);
          log("cmp", `userReset: ${userReset}`);
        }
      }
    }
  };
  if (isInPropertyIdABTest) {
    window._sp_.config.propertyId = getPropertyId(framework);
  }
  switch (framework) {
    case "tcfv2":
      window._sp_.config.gdpr = {
        targetingParams: {
          framework
        }
      };
      break;
    case "usnat":
      window._sp_.config.usnat = {
        targetingParams: {
          framework
        },
        includeUspApi: true,
        transitionCCPAAuth: true
      };
      break;
    case "aus":
      window._sp_.config.ccpa = {
        targetingParams: {
          framework
        }
      };
      break;
  }
  const spLib = document.createElement("script");
  spLib.id = "sourcepoint-lib";
  spLib.src = `${ENDPOINT}/unified/wrapperMessagingWithoutDetection.js`;
  document.body.appendChild(spLib);
};

export { init, willShowPrivacyMessage };
